import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { BASELINE } from "../../util";

const SampleModal = ({ isOpen, onClose, subjects }) => {
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedSubTopic, setSelectedSubTopic] = useState(null);
    const [selectedDifficulty, setSelectedDifficulty] = useState(null);
    const [questionType, setQuestionType] = useState({ value: "MCQ", label: "Multiple Choice" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Reset selections when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedTopic(null);
            setSelectedSubTopic(null);
            setSelectedDifficulty(null);
            setQuestionType({ value: "MCQ", label: "Multiple Choice" });
            setResult(null);
        }
    }, [isOpen]);

    const difficultyOptions = [
        { value: "E", label: "Easy" },
        { value: "M", label: "Medium" },
        { value: "H", label: "Hard" },
    ];

    const questionTypeOptions = [
        { value: "MCQ", label: "Multiple Choice" },
        { value: "FRQ", label: "Free Response" },
    ];

    const topicOptions = subjects.map((subject) => ({
        value: subject.name,
        label: subject.name,
    }));

    const subTopicOptions = selectedTopic
        ? subjects
            .find((subject) => subject.name === selectedTopic.value)
            ?.subTopics.map((subTopic) => ({
                value: subTopic.name,
                label: subTopic.name,
            })) || []
        : [];

    const handleSubmit = () => {
        if (!selectedTopic || !selectedSubTopic || !selectedDifficulty) {
            alert("Please select all options");
            return;
        }

        setLoading(true);

        axios.post(`${BASELINE}api/getSample`, {
            topic: selectedTopic.value,
            subTopic: selectedSubTopic.value,
            difficulty: selectedDifficulty.value,
            questionType: questionType.value
        })
            .then(response => {
                setResult(response.data);
            })
            .catch(error => {
                console.error("Error fetching sample:", error);
                alert("Failed to load sample");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const generatePrompt = () => {
        const difficultyText = selectedDifficulty?.label || "Medium";
        const subTopicText = selectedSubTopic?.value || "the selected topic";
        const qType = questionType?.value || "MCQ";

        let prompt = `You are a test author creating FIVE independent SAT-style ${subTopicText} ${qType === "MCQ" ? "multiple-choice" : "free response"} questions. Follow these rules:

        1. Use the example problems provided above as references. Instead of creating completely new problems, modify the existing example problems by keeping the wording similar and only changing the numeric values (e.g., quantities, numbers in the stem) so that the new questions appear as revised versions of the examples.
        2. Produce exactly FIVE (5) questions.
        3. Each question must be in JSON format with the following structure and keys:
           {
             "stem": "…",
             ${qType === "MCQ" ? `"a": "…",
             "b": "…",
             "c": "…",
             "d": "…",` : `"a": "",
             "b": "",
             "c": "",
             "d": "",`}
             "correct_answer": ${qType === "MCQ" ? `"X"` : `"answer1", "answer2", ..."`},
             "rationale": "…"
           }
        4. The "stem" should contain a clear, well-worded ${subTopicText} problem that tests student understanding at a ${difficultyText} level.
        5. For ${qType === "MCQ" ? "multiple-choice questions" : "free response questions"}, the answer choices ("a", "b", "c", "d") should be as described:
           - If ${qType === "MCQ"}, then these should be four distinct options labeled as A, B, C, and D.
           - If free response, then the "a", "b", "c", and "d" fields should be empty strings.
        6. Additionally, ensure that when creating the five questions, the distribution of correct answers among A, B, C, and D is balanced fairly, with each option serving as the correct answer at least once if possible.
        7. The "correct_answer" field must be in the format:
           - For MCQ: "A", "B", "C", or "D" and must be the best choice.
           - For free response: a JSON array containing all valid answers, e.g., "answer1", "answer2".
        8. The "rationale" must begin with ${qType === "MCQ" ? `"Choice [X] is the best answer. Because..."` : `"The correct answer is [X]. Because..."`} and must be at least 80 tokens long, explaining the solution process thoroughly.
        9. All questions should be independent of each other and based on the provided examples, with the same wording style but with numeric values changed to create new versions. No question should follow from another.
        10. Each problem should be at a ${difficultyText} difficulty level.
        11. Please generate based on the Example. Instead of creating entirely new problems, revise the provided example problems by modifying only the numerical values.
        12. **Important:** Since these are math problems, any numeric value, mathematical expression, or math function appearing in the "stem", answer choices ("a", "b", "c", "d"), or "rationale" must be formatted using LaTeX (KaTeX) syntax. For instance, if a number or equation is mentioned, it should be enclosed in dollar signs (e.g., \`$5$\` or \`$x^2+1$\`).
        
        Now create FIVE modified SAT-style ${subTopicText} questions that meet these requirements.
        
        The Result Must be in JSON format. Do not give any additional words beside the JSON Output.
        `;
        

        return prompt;
    };

    const copyToClipboard = () => {
        if (!result) return;

        // Generate the prompt
        const prompt = generatePrompt();

        // Format sample data
        const sampleData = JSON.stringify(result.data || result, null, 2);

        // Combine sample data and prompt
        const clipboardContent = `SAMPLE DATA:\n${sampleData}\n\nPROMPT:\n${prompt}`;

        navigator.clipboard.writeText(clipboardContent)
            .then(() => {
                alert("Sample data and prompt copied to clipboard!");
            })
            .catch((err) => {
                console.error("Failed to copy:", err);
                alert("Failed to copy to clipboard");
            });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white text-black p-6 rounded-lg w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">Load Sample</h2>

                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Topic</label>
                    <Select
                        options={topicOptions}
                        value={selectedTopic}
                        onChange={(option) => {
                            setSelectedTopic(option);
                            setSelectedSubTopic(null);
                        }}
                        placeholder="Select a topic"
                        className="text-black"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Sub-Topic</label>
                    <Select
                        options={subTopicOptions}
                        value={selectedSubTopic}
                        onChange={setSelectedSubTopic}
                        placeholder="Select a sub-topic"
                        isDisabled={!selectedTopic}
                        className="text-black"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Difficulty</label>
                    <Select
                        options={difficultyOptions}
                        value={selectedDifficulty}
                        onChange={setSelectedDifficulty}
                        placeholder="Select difficulty"
                        className="text-black"
                    />
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium mb-1">Question Type</label>
                    <Select
                        options={questionTypeOptions}
                        value={questionType}
                        onChange={setQuestionType}
                        placeholder="Select question type"
                        className="text-black"
                    />
                </div>

                {result && (
                    <div className="mb-4">
                        <div className="p-3 bg-gray-100 rounded max-h-40 overflow-y-auto">
                            <pre className="text-xs">{JSON.stringify(result, null, 2)}</pre>
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                            <p>Click "Copy Prompt" to copy both the sample data and template for generating a similar question.</p>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    {result && (
                        <button
                            onClick={copyToClipboard}
                            className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
                        >
                            Copy Sample & Prompt
                        </button>
                    )}

                    {!result ? (
                        <button
                            onClick={handleSubmit}
                            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
                            disabled={loading || !selectedTopic || !selectedSubTopic || !selectedDifficulty}
                        >
                            {loading ? "Loading..." : "Submit"}
                        </button>
                    ) : null}

                    <button
                        onClick={onClose}
                        className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SampleModal;